import { PanelItem } from '../panel-item.class'

/**
 * Inset from the panel edge left when an overhanging group is pulled back in.
 * Only ever a starting position: every path that adds an item runs
 * `distributeApplianceGroupsHorizontally` immediately afterwards, which re-lays
 * the whole run out. It matters solely for ordering — the group has to land on
 * the side it was aimed at so the distribute sort keeps it there.
 */
const EDGE_INSET = 3;

/**
 * Pull a group back inside the panel when the item just added to it would
 * otherwise hang off the end.
 *
 * PASSES AN ABSOLUTE POSITION, NOT A DELTA. `PanelItemGroup.move` assigns
 * `center.x = position.x`; it does not add to the current position. This used
 * to compute how far the group needed to shift and hand that shift to `move`,
 * so a group parked at the right edge was not pulled in — it was teleported to
 * the shift amount, which is a small NEGATIVE number, i.e. to the left of
 * centre.
 *
 * That is what broke "put it on the far right". `addApplianceTool` sets the new
 * group's center to `panelWidth/2 - 1` to express the intent, then adds the
 * item; this function fired on the resulting overhang and moved the group to
 * (for an 84" panel and a 14" appliance) x = -9. The following distribute sorts
 * groups by `center.x`, so the appliance the customer asked for at the far end
 * came to rest in the middle of the run, and they had to ask a second time.
 * "Far left" failed the same way mirrored.
 *
 * The corrected target is what the old arithmetic was reaching for: the old
 * `delta` satisfies `currentX + delta === panelHalf - itemHalf - EDGE_INSET`,
 * so that expression is used directly.
 */
export const positionHorizontallyCfn = (panelItem: PanelItem): void => {
	const panelItemGroup = panelItem.refMap?.panelItemGroup;
	const panel = panelItem.refMap?.panel;
	if (!panelItemGroup || !panel) return;

	const currentPosition = panelItemGroup.center;
	if (!currentPosition) return;

	const itemWidthHalf = panelItem.item.size.width / 2;
	const panelWidthHalf = panel.size.width / 2;

	const overhangsRight = currentPosition.x > 0 && currentPosition.x + itemWidthHalf > panelWidthHalf;
	const overhangsLeft = currentPosition.x <= 0 && currentPosition.x - itemWidthHalf < -panelWidthHalf;

	if (overhangsRight) {
		panelItemGroup.move({ x: panelWidthHalf - itemWidthHalf - EDGE_INSET });
	} else if (overhangsLeft) {
		panelItemGroup.move({ x: -panelWidthHalf + itemWidthHalf + EDGE_INSET });
	}
}
